import { Alert, Button, Card, Modal } from "imagine-ui";
import { UserRole, UserSelector } from "imagine-users";
import {
  Address,
  AddressInfo,
  AddressInfoCards,
  CommentEditor,
  CommentsApi,
  Defect,
  DefectComment,
  DefectEditor,
  DefectInfo,
  DefectStatus,
  DefectStatusEntry,
  DefectsApi,
  EditButton,
  NavigateBack,
  Project,
  ProjectInfo,
  ProjectsApi,
} from "punchlist";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "src";

export const DefectInfoContainer = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCommentEditor, setShowCommentEditor] = useState(false);
  const [commentErrorMessage, setCommentErrorMessage] = useState("");
  const [defect, setDefect] = useState<Defect | undefined>(undefined);

  const loadDefect = useCallback(async () => {
    console.log("Loading defect", params);
    if (params.addressCode && params.defectId) {
      const addressCode = params.addressCode;
      const defectId = parseInt(params.defectId);
      console.log("Loading defect", addressCode);
      const response = await DefectsApi.getDefect(defectId);
      if (response.success && response.data) {
        setDefect(response.data);
        console.log("Loaded defect", response.data);
        return;
      } else {
        setErrorMessage(t("punchlist:defectNotFound"));
      }
    }
  }, [params]);

  const updateStatus = async (status: DefectStatusEntry) => {
    if (!defect?.addressCode) return;
    const response = await DefectsApi.updateStatus(
      defect.addressCode,
      defect.id,
      { ...status, userRole: UserRole.NONE, userId: 0 }
    );
    if (response.success && response.data) {
      await loadDefect();
      console.log("Updated defect status", response.data);
    } else {
      setErrorMessage(t("punchlist:defectStatusUpdateFailed"));
    }
  };

  const addComment = async (comment: DefectComment, images: File[]) => {
    console.log("Adding comment", comment, images);
    setSavingComment(true);
    setCommentErrorMessage("");
    const response = await CommentsApi.addComment({
      ...comment,
      userRole: UserRole.NONE,
    });
    if (response.success && response.data) {
      console.log("Added comment", response.data);
      if (images.length > 0) {
        setUploading(true);
        const imageResponse = await CommentsApi.uploadImages(
          response.data.id,
          images
        );
        setUploading(false);
        if (!imageResponse.success) {
          setCommentErrorMessage(
            imageResponse.errorMessage || t("punchlist:imageUploadFailed")
          );
        }
      }
      loadDefect();
      setShowCommentEditor(false);
    } else {
      setCommentErrorMessage(t("punchlist:commentAddFailed"));
    }
    setSavingComment(false);
  };

  useEffect(() => {
    loadDefect();
  }, [loadDefect]);

  return (
    <>
      {defect && (
        <>
          <div className="row items-space-between mb items-y-center">
            <NavigateBack
              to={"/" + params.addressCode}
              text={t("punchlist:backToOverview")}
            />
            {/* <EditButton
              to={"/" + params.addressCode + "/" + defect.id + "/edit"}
            /> */}
          </div>
          <DefectInfo
            defect={defect}
            userRole={UserRole.NONE}
            onSave={updateStatus}
            showStatusHistory
          />
          {!showCommentEditor && (
            <Button
              outline
              onClick={() => setShowCommentEditor(true)}
              className="mt-lg"
            >
              {t("punchlist:addComment")}
            </Button>
          )}
          {showCommentEditor && (
            <CommentEditor
              className="mt-lg"
              defectId={defect.id}
              onSave={addComment}
              errorMessage={commentErrorMessage}
              saving={savingComment}
            />
          )}
          {!showCommentEditor && commentErrorMessage && (
            <Alert color="danger" className="mt-lg">
              {commentErrorMessage}
            </Alert>
          )}
          {uploading && (
            <Modal visible hideFooter hideHeader>
              <p>{t("punchlist:uploadingImages")}</p>
            </Modal>
          )}
        </>
      )}
    </>
  );
};
